export const getPreselectedRange = (
  preselectedRange,
  includeFuture = false,
) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let start = new Date(today);
  let end = new Date(today);

  const setToMonday = (date) => {
    const dayOfWeek = date.getDay();
    const distance = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    date.setDate(date.getDate() - distance);
  };

  // naive approach for now
  switch (preselectedRange) {
    case "lastSeven":
      start.setDate(start.getDate() - 6);
      break;

    case "lastThirty":
      start.setDate(start.getDate() - 29);
      break;

    case "lastNinety":
      start.setDate(start.getDate() - 89);
      break;

    case "thisWeek": {
      setToMonday(start);
      end = new Date(today);
      if (includeFuture) {
        end.setDate(end.getDate() + 6);
      }
      break;
    }

    case "lastWeek": {
      setToMonday(start);
      start.setDate(start.getDate() - 7);
      end = new Date(start);
      end.setDate(end.getDate() + 6);
      break;
    }

    case "thisMonth": {
      const y = start.getFullYear();
      const m = start.getMonth();
      start = new Date(y, m, 1);
      end = includeFuture ? new Date(y, m + 1, 0) : new Date(today);
      break;
    }

    case "lastMonth": {
      const y = start.getFullYear();
      const m = start.getMonth();
      start = new Date(y, m - 1, 1);
      end = new Date(y, m, 0);
      break;
    }

    default:
      break;
  }

  return { start, end };
};

export const constructCalendar = (
  year,
  month,
  start,
  end,
  includeFuture = false,
  includePast = true,
  isRangePicker = true,
) => {
  const weeks = [];
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  const firstDayOfWeek = firstOfMonth.getDay();
  const gridStartDate = new Date(firstOfMonth);
  gridStartDate.setDate(gridStartDate.getDate() - firstDayOfWeek);

  const lastDayOfWeek = lastOfMonth.getDay();
  const gridEndDate = new Date(lastOfMonth);
  gridEndDate.setDate(gridEndDate.getDate() + (6 - lastDayOfWeek));

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const isSameDay = (d1, d2) =>
    d1 &&
    d2 &&
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const isBetween = (target, dStart, dEnd) =>
    dStart && dEnd && dStart <= target && target <= dEnd;

  const sameCalendarMonth = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();

  const shouldBeHidden = (currentMonth, currentYear) =>
    currentYear > year ||
    (currentYear === year && currentMonth < month) ||
    currentMonth > month;

  const currentDate = new Date(gridStartDate);

  while (currentDate <= gridEndDate) {
    const week = [];

    for (let i = 0; i < 7; i += 1) {
      const dateClone = new Date(currentDate);
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      const firstDayCurrentMonth = new Date(currentYear, currentMonth, 1);
      const lastDayCurrentMonth = new Date(currentYear, currentMonth + 1, 0);

      const dayIsInSelectedRange = isBetween(currentDate, start, end);

      const prevDay = new Date(currentDate);
      prevDay.setDate(prevDay.getDate() - 1);
      const wasInRange = isBetween(prevDay, start, end);

      const nextDay = new Date(currentDate);
      nextDay.setDate(nextDay.getDate() + 1);
      const willBeInRange = isBetween(nextDay, start, end);

      const firstDay = dayIsInSelectedRange && !wasInRange;
      const lastDay = dayIsInSelectedRange && !willBeInRange;

      const isRangeStart =
        isSameDay(currentDate, start) ||
        firstDay ||
        (dayIsInSelectedRange &&
          wasInRange &&
          !sameCalendarMonth(prevDay, currentDate) &&
          !shouldBeHidden(prevDay, start));

      const isRangeEnd =
        isSameDay(currentDate, end) ||
        lastDay ||
        (dayIsInSelectedRange &&
          willBeInRange &&
          !sameCalendarMonth(nextDay, currentDate) &&
          !shouldBeHidden(nextDay, start));

      const dayObj = {
        date: currentDate.getDate(),
        fullDate: dateClone,
        notCurrentMonth: currentMonth !== month || currentYear !== year,
        hidden: shouldBeHidden(currentMonth, currentYear),
        disabled:
          !!(!includeFuture && currentDate > now) ||
          !!(!includePast && currentDate < now),
        isRangeStart,
        isRangeEnd,
        isInRange: dayIsInSelectedRange,
        isSoloStart: isSameDay(currentDate, start) && !end,
        isRightEdge:
          i === 6 || lastDayCurrentMonth.getDate() === currentDate.getDate(),
        isLeftEdge:
          i === 0 || firstDayCurrentMonth.getDate() === currentDate.getDate(),
        isCurrentDate:
          currentYear === now.getFullYear() &&
          currentMonth === now.getMonth() &&
          currentDate.getDate() === now.getDate(),
        isSelected: !isRangePicker && isSameDay(currentDate, start),
      };

      week.push(dayObj);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    weeks.push(week);
  }

  if (weeks.length < 6) weeks.push([[]]); // so height doesn't change

  return weeks;
};

export const datePreselects = {
  lastSeven: "Last 7 days",
  lastThirty: "Last 30 days",
  lastNinety: "Last 90 days",
  thisWeek: "This Week",
  lastWeek: "Last Week",
  thisMonth: "This Month",
  lastMonth: "Last Month",
};
